import { useState } from 'react';
import type { FormEvent } from 'react';
import { isAxiosError } from 'axios';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore, useIsAuthenticated } from '~/lib/auth/auth-store';
import { useSidebarStore } from '~/lib/store/sidebar';
import sleedLogo from '~/assets/sleed-logo.png';

/** Minimal login (spec I.1). Full auth UX redesign is a later session. */
export function Login() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const signIn = useAuthStore(s => s.signIn);
	const resetExpanded = useSidebarStore(s => s.resetExpanded);
	const isAuthenticated = useIsAuthenticated();

	const [userName, setUserName] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
	if (isAuthenticated) return <Navigate to={from} replace />;

	const onSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			await signIn(userName, password);
			resetExpanded(); // start every session with only the Orders group expanded
			navigate(from, { replace: true });
		} catch (err) {
			// A 429 is a wait notice, not a credentials error: show the server's localized message (it carries the seconds
			// to wait). Every other failure keeps the generic text, so it does not reveal which accounts exist.
			const throttled = isAxiosError<{ msg?: string }>(err) && err.response?.status === 429 ? err.response.data?.msg : undefined;
			setError(throttled || t('auth.login.error'));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex min-h-screen items-center justify-center bg-background p-4">
			<form onSubmit={onSubmit} className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-elevation-2">
				<div className="mb-5 flex flex-col items-center gap-2 text-center">
					{/* Black-on-transparent wordmark; `brightness-0 invert` flips it to white in dark theme. */}
					<img src={sleedLogo} alt="Sleed" className="mb-1 h-9 w-auto object-contain dark:brightness-0 dark:invert" />
					<h1 className="text-xl font-semibold text-foreground">{t('auth.login.title')}</h1>
					<p className="text-sm text-foreground-muted">{t('auth.login.subtitle')}</p>
				</div>

				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="login-username">{t('auth.login.username')}</Label>
						<Input id="login-username" autoComplete="username" value={userName} onChange={e => setUserName(e.target.value)} autoFocus />
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="login-password">{t('auth.login.password')}</Label>
						<Input
							id="login-password"
							type="password"
							autoComplete="current-password"
							value={password}
							onChange={e => setPassword(e.target.value)}
						/>
					</div>

					{error && <p className="text-sm text-danger-fg">{error}</p>}

					<Button type="submit" disabled={loading || !userName || !password} className="mt-1">
						{loading && <Loader2 size={16} className="animate-spin" />}
						{t('auth.login.submit')}
					</Button>
				</div>
			</form>
		</div>
	);
}
